import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Autocomplete, getFilterOptions } from "@msys/ui";
import { Stack } from "@mui/material";
import {
  ProjectSourcePageName,
  namedOperations,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { useProjectSelectQuery } from "./ProjectSelect.generated.js";
import { CreateProjectButton } from "./buttons/CreateProjectButton.js";
import { useCreateProjectModal_CrmCompanySingleQuery } from "./modals/CreateProjectModal.generated.js";

const filterOptionsByTitle = getFilterOptions(["title"]);

export function ProjectSelect({
  crmCompanyId,
  projectId,
  onChange,
  inputLabel,
  placeholder,
  required,
  error,
  disabled,
  canCreateNew,
  pageName,
  onCreateRefetchQueries,
  onCreateAutoNavigate,
  desiredProjectInitialStatus,
}: {
  crmCompanyId?: string;
  projectId: string | null;
  onChange: (projectId: string | null) => void;
  inputLabel: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  canCreateNew?: boolean;
  pageName: ProjectSourcePageName;
  onCreateRefetchQueries?: string[];
  onCreateAutoNavigate?: boolean;
  desiredProjectInitialStatus?: "opportunity" | "contracted";
}) {
  const client = useApolloClient();
  const query = useProjectSelectQuery({
    client,
    variables: { crmCompanyId },
  });
  const projects =
    getDataOrNull(query.data?.projects)?.edges.map(edge => edge.node) ?? [];
  const crmCompanyQuery = useCreateProjectModal_CrmCompanySingleQuery({
    client,
    variables: { crmCompanyId: crmCompanyId! },
    skip: !crmCompanyId,
  });
  const crmOrganisation = getDataOrNull(
    crmCompanyQuery?.data?.crmCompany
  )?.crmCompany;

  return (
    <Stack direction={"row"} spacing={1}>
      <Autocomplete
        inputLabel={inputLabel}
        placeholder={placeholder}
        required={required}
        options={projects}
        error={error}
        getOptionLabel={option => option.title}
        filterOptions={filterOptionsByTitle}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option.id}
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {option.title}
          </li>
        )}
        value={projects.find(project => project.id === projectId)}
        onChange={value => onChange(value?.id ?? null)}
        disabled={disabled || query.loading}
      />
      {canCreateNew && (
        <RestrictedByOrganisationPermissionWithDebug permission="CREATE_PROJECT">
          <CreateProjectButton
            type="icon"
            crmOrganisation={crmOrganisation ?? undefined}
            size="large"
            pageName={pageName}
            refetchQueries={[
              ...(onCreateRefetchQueries ?? []),
              namedOperations.Query.ProjectSelect,
            ]}
            desiredProjectInitialStatus={desiredProjectInitialStatus}
            autoNavigate={onCreateAutoNavigate}
            onCreate={projectId => {
              onChange(projectId);
            }}
          />
        </RestrictedByOrganisationPermissionWithDebug>
      )}
    </Stack>
  );
}
